import React from 'react';
import { JobWithClient } from '../types/jobs';
import { useTranslation } from '../hooks/useTranslation';

interface JobScheduleProps {
  jobs: JobWithClient[];
  title: string;
  emptyMessage: string;
  onJobClick?: (job: JobWithClient) => void;
}

const JobSchedule: React.FC<JobScheduleProps> = ({ 
  jobs, 
  title, 
  emptyMessage, 
  onJobClick 
}) => {
  const { t } = useTranslation();

  const formatTime = (timeString?: string) => {
    if (!timeString) return '-';
    return new Date(timeString).toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (minutes?: number) => {
    if (!minutes) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getJobTypeColor = (jobType: string) => {
    switch (jobType) {
      case 'scheduled':
        return 'bg-purple-100 text-purple-800';
      case 'flexible':
        return 'bg-orange-100 text-orange-800';
      case 'ad-hoc':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (jobs.length === 0) {
    return (
      <div className="card">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">{title}</h2>
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-sm text-gray-600">{emptyMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">{title}</h2>
      <div className="space-y-3">
        {jobs.map((job) => (
          <div
            key={job.jobId}
            className={`border rounded-lg p-4 cursor-pointer transition-colors ${
              onJobClick 
                ? 'hover:bg-gray-50 hover:border-gray-300' 
                : ''
            }`}
            onClick={() => onJobClick?.(job)}
          >
            <div className="flex justify-between items-start mb-2">
              <div className="flex-1">
                <h3 className="font-medium text-gray-900">
                  {job.clientName || job.clientId}
                </h3>
                <p className="text-sm text-gray-600">
                  {job.clientEmail || 'Cliente'}
                </p>
              </div>
              <div className="flex space-x-2">
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getJobTypeColor(job.jobType)}`}>
                  {t(job.jobType === 'scheduled' ? 'scheduledJobs' : job.jobType === 'flexible' ? 'flexibleJobs' : 'adHocJobs')}
                </span>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(job.status)}`}>
                  {t(job.status)}
                </span>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">{t('startTime')}:</span>
                <span className="ml-1 font-medium">{formatTime(job.expectedStartTime)}</span>
              </div>
              <div>
                <span className="text-gray-600">{t('endTime')}:</span>
                <span className="ml-1 font-medium">{formatTime(job.expectedEndTime)}</span>
              </div>
              <div>
                <span className="text-gray-600">{t('duration')}:</span>
                <span className="ml-1 font-medium">{formatDuration(job.expectedDuration)}</span>
              </div>
              <div>
                <span className="text-gray-600">{t('jobType')}:</span>
                <span className="ml-1 font-medium">{t(job.jobType === 'scheduled' ? 'scheduledJobs' : job.jobType === 'flexible' ? 'flexibleJobs' : 'adHocJobs')}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default JobSchedule; 